<script lang="ts">
  /**
   * The leaderboard (brief §17.1).
   *
   * A tab of its own next to the map, not a line in a profile menu. The default is the board
   * the project was asked for: every user, all time, by attribute ratings given — so a careful
   * report with six attributes is worth six and a lazy one is worth one.
   *
   * The reader's own row is pinned to the bottom edge however far down it is. A leaderboard
   * you cannot find yourself on is a leaderboard you stop opening.
   */
  import { request } from './api.ts';
  import Avatar from './Avatar.svelte';
  import { describeError } from './session.svelte.ts';

  type Row = {
    rank: number;
    userId: string;
    username: string;
    avatarEmoji: string;
    score: number;
    me: boolean;
  };

  type Props = { onclose: () => void };
  const { onclose }: Props = $props();

  const BOARDS = [
    { id: 'ratings', label: 'Bewertungen' },
    { id: 'sightings', label: 'Meldungen' },
    { id: 'confirmed', label: 'Bestätigt' },
    { id: 'cells', label: 'Neue Waben' },
    { id: 'swipes', label: 'Gewischt' },
    // Swipes where most of the people before you had judged the place the same way.
    { id: 'crowd', label: 'Schwarmgefühl' },
  ] as const;

  const PERIODS = [
    { id: 'all', label: 'Gesamt' },
    { id: 'month', label: 'Monat' },
    { id: 'week', label: 'Woche' },
  ] as const;

  let board = $state<(typeof BOARDS)[number]['id']>('ratings');
  let period = $state<(typeof PERIODS)[number]['id']>('all');
  let rows = $state<Row[]>([]);
  let loading = $state(true);
  let error = $state('');

  const mine = $derived(rows.find((row) => row.me));

  $effect(() => {
    const query = new URLSearchParams({ board, period });
    void (async () => {
      loading = true;
      try {
        rows = (await request<{ rows: Row[] }>(`/api/leaderboard?${query}`)).rows;
        error = '';
      } catch (cause) {
        error = describeError(cause);
      } finally {
        loading = false;
      }
    })();
  });

  const MEDALS = ['🥇', '🥈', '🥉'];
</script>

<section class="panel" aria-label="Rangliste">
  <header>
    <h2>Rangliste</h2>
    <button class="close" onclick={onclose} aria-label="Schließen">×</button>
  </header>

  <nav class="boards" aria-label="Wertung">
    {#each BOARDS as entry (entry.id)}
      <button aria-pressed={board === entry.id} onclick={() => (board = entry.id)}>
        {entry.label}
      </button>
    {/each}
  </nav>

  <nav class="periods" aria-label="Zeitraum">
    {#each PERIODS as entry (entry.id)}
      <button aria-pressed={period === entry.id} onclick={() => (period = entry.id)}>
        {entry.label}
      </button>
    {/each}
  </nav>

  {#if error}
    <p class="hint error" role="alert">{error}</p>
  {:else if loading}
    <p class="hint">Einen Moment …</p>
  {:else if rows.length === 0}
    <p class="hint">Noch keine Punkte. Sei die Erste.</p>
  {:else}
    <ol>
      {#each rows as row (row.userId)}
        <li class:me={row.me}>
          <span class="rank">{MEDALS[row.rank - 1] ?? row.rank}</span>
          <span class="who">
            <Avatar username={row.username} emoji={row.avatarEmoji} size={22} /> @{row.username}
          </span>
          <span class="score">{row.score}</span>
        </li>
      {/each}
    </ol>
  {/if}

  {#if mine !== undefined}
    <!-- Pinned, because the whole point of a ranking is knowing where you stand. -->
    <p class="own">
      <span class="rank">{mine.rank}</span>
      <span class="who">
        <Avatar username={mine.username} emoji={mine.avatarEmoji} size={22} /> @{mine.username}
      </span>
      <span class="score">{mine.score}</span>
    </p>
  {/if}
</section>

<style>
  .panel {
    position: absolute;
    inset: max(3.5rem, env(safe-area-inset-top)) 1rem max(1rem, env(safe-area-inset-bottom));
    z-index: 4;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-width: 30rem;
    margin-inline: auto;
    padding: 0.75rem 1rem 1rem;
    border: 1px solid var(--border);
    border-radius: 1rem;
    background: var(--card);
    box-shadow: 0 8px 32px rgb(0 0 0 / 30%);
  }

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  h2 {
    margin: 0;
    font-size: 1.1rem;
  }

  .close {
    border: 0;
    background: none;
    color: var(--muted);
    font-size: 1.75rem;
    line-height: 1;
    cursor: pointer;
  }

  .boards,
  .periods {
    display: flex;
    gap: 0.3rem;
    flex-wrap: wrap;
  }

  .boards button,
  .periods button {
    padding: 0.3rem 0.6rem;
    border: 1px solid var(--border);
    border-radius: 999px;
    background: var(--bg);
    color: var(--muted);
    font-size: 0.75rem;
    cursor: pointer;
  }

  .boards button[aria-pressed='true'],
  .periods button[aria-pressed='true'] {
    border-color: var(--accent);
    color: var(--fg);
  }

  ol {
    flex: 1;
    overflow-y: auto;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li,
  .own {
    display: grid;
    grid-template-columns: 2rem 1fr auto;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    padding: 0.4rem 0.2rem;
    font-size: 0.9375rem;
  }

  li + li {
    border-top: 1px solid var(--border);
  }

  li.me {
    color: var(--accent);
  }

  .own {
    border-top: 2px solid var(--accent);
    padding-top: 0.6rem;
    font-weight: 600;
  }

  .rank {
    color: var(--muted);
    text-align: center;
  }

  .score {
    font-variant-numeric: tabular-nums;
  }

  .hint {
    margin: 0;
    font-size: 0.8125rem;
    color: var(--muted);
  }

  .error {
    color: var(--accent);
  }
</style>
